import type { AnchorHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { buttonClassName } from '@argink/ui';
import type { ButtonAppearance } from '@argink/ui';

/**
 * `@argink/ui`'s `Button` só renderiza `<button>` — não tem `asChild` (não é
 * Radix-style). Navegação in-app é semanticamente um link (preview de URL,
 * "abrir em nova aba", etc.), então isto aplica a um `<Link>` de verdade a
 * MESMA aparência de `Button`, em vez de aninhar `<a>` dentro de `<button>`
 * (HTML inválido) ou reimplementar o visual.
 *
 * **R-UX02-P01/L3A-FIX (achado P2 do gate G3A):** antes deste arquivo copiava
 * à mão os nomes de classe (`argink-btn--${variant}`) e reimplementava os
 * tipos `ButtonVariant`/`ButtonSize` — exatamente o acoplamento a nomes
 * internos que o comentário original de `Button.tsx` dizia evitar, e sem a
 * proteção de contraste que `buttonClassName` já tinha (forçar `size="lg"` em
 * `variant="accent"`, ver `Button.tsx`). Agora consome `buttonClassName` +
 * `ButtonAppearance` direto de `@argink/ui` — a única fonte de verdade da
 * aparência, que `Button` usa internamente também (não podem mais divergir).
 */
export interface LinkButtonProps
  extends LinkProps,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'color'>,
    ButtonAppearance {}

export function LinkButton({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className,
  ...rest
}: LinkButtonProps) {
  return <Link className={buttonClassName({ variant, size, fullWidth, className })} {...rest} />;
}
